import { useEffect, useMemo, useRef, useState } from 'react'
import { Plus, X } from 'lucide-react'
import { Input } from '@/shared/components/atoms/ui/input'
import { Button } from '@/shared/components/atoms/ui/button'
import { requestCenterApi } from '@/features/request-center'

/**
 * Multi-value picker backed by a data-source search (e.g. `employees.active`,
 * `entra.groups`). Persists chosen values as the data-source's `value`
 * (typically a UUID); displays them as chips with the human `label`.
 *
 * Behaviour:
 *  - Type to search → debounced 200ms call to `searchDataSource(key, { q })`
 *  - Click a result → adds it to the chip list, clears the input
 *  - Click the × on a chip → removes it
 *  - Already-persisted values that don't match a search hit are shown as
 *    chips with their raw value (the admin can still remove them); a
 *    background lookup tries to resolve their label via a no-query search
 *    (best-effort — falls back to the raw value if the data source
 *    doesn't support that lookup mode)
 *
 * Usage:
 *   <MultiSourceAutocomplete
 *     dataSourceKey="employees.active"
 *     placeholder="Search employees by name…"
 *     ariaPrefix="specificPeople"
 *     values={step.specificPeople ?? []}
 *     onChange={(next) => onPatch({ specificPeople: next })}
 *   />
 */
export interface MultiSourceAutocompleteProps {
	dataSourceKey: string
	placeholder?: string
	ariaPrefix: string
	values: ReadonlyArray<string>
	onChange: (next: ReadonlyArray<string>) => void
}

interface SearchHit {
	value: string
	label: string
}

const SEARCH_DEBOUNCE_MS = 200
const MAX_RESULTS = 20

export function MultiSourceAutocomplete({
	dataSourceKey,
	placeholder = 'Search…',
	ariaPrefix,
	values,
	onChange,
}: MultiSourceAutocompleteProps) {
	const [query, setQuery] = useState('')
	const [results, setResults] = useState<SearchHit[]>([])
	const [isOpen, setIsOpen] = useState(false)
	const [isSearching, setIsSearching] = useState(false)
	// `labels[value] = label`. Looked-up display names for already-saved
	// values (so chips render as names, not UUIDs).
	const [labels, setLabels] = useState<Record<string, string>>({})
	const containerRef = useRef<HTMLDivElement>(null)
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	// Close dropdown on outside click.
	useEffect(() => {
		const handler = (e: MouseEvent) => {
			if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
				setIsOpen(false)
			}
		}
		document.addEventListener('mousedown', handler)
		return () => document.removeEventListener('mousedown', handler)
	}, [])

	// Debounced search.
	useEffect(() => {
		if (debounceRef.current) clearTimeout(debounceRef.current)
		const trimmed = query.trim()
		if (trimmed === '') {
			setResults([])
			setIsSearching(false)
			return
		}
		setIsSearching(true)
		debounceRef.current = setTimeout(() => {
			void runSearch(dataSourceKey, trimmed).then((hits) => {
				setResults(hits)
				setIsSearching(false)
				// Cache labels we just discovered so chips can render them.
				setLabels((prev) => {
					const next = { ...prev }
					for (const h of hits) next[h.value] = h.label
					return next
				})
			})
		}, SEARCH_DEBOUNCE_MS)
		return () => {
			if (debounceRef.current) clearTimeout(debounceRef.current)
		}
	}, [query, dataSourceKey])

	// On mount (and when values change), look up labels for any persisted
	// values we don't yet have a label for. We do this by issuing a single
	// no-query search and matching by `value` — best-effort; data sources
	// that don't return all rows on empty query just leave the chip
	// showing the raw value.
	useEffect(() => {
		const missing = values.filter((v) => !labels[v])
		if (missing.length === 0) return
		void runSearch(dataSourceKey, '').then((hits) => {
			const found: Record<string, string> = {}
			for (const v of missing) {
				const hit = hits.find((h) => h.value === v)
				if (hit) found[v] = hit.label
			}
			if (Object.keys(found).length > 0) {
				setLabels((prev) => ({ ...prev, ...found }))
			}
		})
		// `labels` is intentionally omitted from deps — including it would
		// re-fire whenever a label resolves, which would loop. The
		// react-hooks/exhaustive-deps rule isn't configured in this repo,
		// so a disable directive isn't needed (and would lint-error).
	}, [values, dataSourceKey])

	const visibleResults = useMemo(() => {
		const taken = new Set(values)
		return results.filter((r) => !taken.has(r.value)).slice(0, MAX_RESULTS)
	}, [results, values])

	const addValue = (hit: SearchHit) => {
		if (values.includes(hit.value)) return
		onChange([...values, hit.value])
		setQuery('')
		setResults([])
		setIsOpen(false)
	}

	const removeAt = (index: number) => {
		const next = [...values]
		next.splice(index, 1)
		onChange(next)
	}

	return (
		<div ref={containerRef} className="space-y-2">
			{values.length > 0 ? (
				<ul className="flex flex-wrap gap-1">
					{values.map((v, i) => (
						<li
							key={`${v}-${i}`}
							className="flex items-center gap-1 rounded-full border bg-background px-2 py-0.5 text-xs"
							data-testid={`${ariaPrefix}-chip-${i + 1}`}
						>
							<span className="truncate">{labels[v] ?? v}</span>
							<button
								type="button"
								onClick={() => removeAt(i)}
								aria-label={`Remove ${ariaPrefix} entry ${i + 1}`}
								className="text-muted-foreground hover:text-destructive"
							>
								<X className="size-3" />
							</button>
						</li>
					))}
				</ul>
			) : (
				<p className="text-xs text-muted-foreground">None.</p>
			)}

			<div className="relative">
				<Input
					type="text"
					value={query}
					onChange={(e) => {
						setQuery(e.target.value)
						setIsOpen(true)
					}}
					onFocus={() => setIsOpen(true)}
					placeholder={placeholder}
					aria-label={`Search ${ariaPrefix}`}
				/>
				{isOpen && (visibleResults.length > 0 || isSearching || query.trim().length > 0) ? (
					<div className="absolute left-0 right-0 top-full z-10 mt-1 max-h-60 overflow-auto rounded-md border bg-popover shadow-md">
						{isSearching ? (
							<p className="p-2 text-xs text-muted-foreground">Searching…</p>
						) : visibleResults.length === 0 ? (
							<p className="p-2 text-xs text-muted-foreground">No matches.</p>
						) : (
							<ul role="listbox">
								{visibleResults.map((hit) => (
									<li key={hit.value}>
										<button
											type="button"
											onClick={() => addValue(hit)}
											className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-sm hover:bg-accent"
											aria-label={`Add ${hit.label}`}
										>
											<Plus className="size-3 text-muted-foreground" />
											<span className="truncate">{hit.label}</span>
											<span className="ml-auto truncate font-mono text-[10px] text-muted-foreground/60">
												{hit.value}
											</span>
										</button>
									</li>
								))}
							</ul>
						)}
					</div>
				) : null}
			</div>

			{/* Manual-entry fallback: lets the admin paste a UUID/group id directly
			    (e.g. for placeholder strings used by seeds before real ids exist). */}
			<details className="text-xs">
				<summary className="cursor-pointer text-muted-foreground hover:text-foreground">Add a raw id manually</summary>
				<RawAddRow
					ariaPrefix={ariaPrefix}
					onAdd={(raw) => {
						const v = raw.trim()
						if (v === '' || values.includes(v)) return
						onChange([...values, v])
					}}
				/>
			</details>
		</div>
	)
}

function RawAddRow({ ariaPrefix, onAdd }: { ariaPrefix: string; onAdd: (raw: string) => void }) {
	const [raw, setRaw] = useState('')
	return (
		<div className="mt-1 flex items-center gap-1">
			<Input
				type="text"
				value={raw}
				onChange={(e) => setRaw(e.target.value)}
				placeholder="UUID or group id"
				aria-label={`Add ${ariaPrefix} entry by raw id`}
				className="h-8 text-xs"
			/>
			<Button
				type="button"
				size="sm"
				variant="outline"
				onClick={() => {
					onAdd(raw)
					setRaw('')
				}}
				aria-label={`Add ${ariaPrefix} entry`}
			>
				<Plus className="size-3" />
			</Button>
		</div>
	)
}

async function runSearch(dataSourceKey: string, q: string): Promise<SearchHit[]> {
	try {
		const res = await requestCenterApi.searchDataSource(dataSourceKey, { q, limit: MAX_RESULTS })
		return (res.data ?? []).map((row) => ({ value: row.value, label: row.label }))
	} catch {
		return []
	}
}
